/**
 * My Trades' Polymarket US realized P&L (#1031), from the relay's positions read, in the K/P headline
 * shape: only closed rows count, as for K/P (settled or fully sold positions; an open position is not
 * realized yet). A closed row with an unknown realized value (a settled position whose payout is not
 * verified yet, or one whose cost basis or fees are unknown) is pending, and a pending row makes the
 * venue's amount null — never a partial total, never counted as 0. Sums are exact (12 places, as the
 * relay); only the final total is rounded to cents.
 */

import type { UsMyTradeFill, UsMyTradePosition } from '@/lib/polymarketUs/myTradesClient';

export interface UsPnlSummary {
  /** Sum of the in-range totals in cents; null when any position is pending. */
  netPnlCents: number | null;
  /** Settled and closed (fully sold) positions in range. */
  closedCount: number;
  /** Closed positions in range whose realized value is unknown (the two counts below). */
  pendingCount: number;
  /** Settled positions whose P&L waits on a verified payout. */
  pendingPayoutCount: number;
  /** Other positions with an unknown realized value (cost basis or fees unknown, orphan sale). */
  incompleteCount: number;
}

const SCALE = 10n ** 12n;
const SIGNED = /^(-?)(\d+)(?:\.(\d{1,12}))?$/;

function units(value: string): bigint {
  const m = SIGNED.exec(value);
  if (!m) throw new Error('US_DECIMAL_INVALID');
  const magnitude = BigInt(m[2]) * SCALE + BigInt((m[3] ?? '').padEnd(12, '0'));
  return m[1] ? -magnitude : magnitude;
}

/** Exact 12-place units to whole cents, half away from zero. */
function toCents(total: bigint): number {
  const perCent = SCALE / 100n;
  const negative = total < 0n, abs = negative ? -total : total;
  const cents = (abs + perCent / 2n) / perCent;
  return Number(negative ? -cents : cents);
}

function inRange(iso: string | null, startMs: number, endMs: number): boolean {
  // A position with no time at all is only shown when the window is unbounded.
  if (iso === null) return startMs <= 0 && endMs === Infinity;
  const ms = Date.parse(iso);
  return ms >= startMs && ms <= endMs;
}

/** Positions whose realized value falls in the window: settled ones by settlement time, open ones by last fill. */
export function usPositionsInRange(positions: readonly UsMyTradePosition[], startMs: number, endMs: number): UsMyTradePosition[] {
  return positions.filter((p) => inRange(p.settledAt ?? p.lastFillAt, startMs, endMs));
}

/** Fills by venue time, else by G1's receipt time. */
export function usFillsInRange(fills: readonly UsMyTradeFill[], startMs: number, endMs: number): UsMyTradeFill[] {
  return fills.filter((f) => {
    const ms = f.executedAt === null ? f.observedAtMs : Date.parse(f.executedAt);
    return ms >= startMs && ms <= endMs;
  });
}

export function summarizeUsPnl(positions: readonly UsMyTradePosition[]): UsPnlSummary {
  let total = 0n, closedCount = 0, pendingPayoutCount = 0, incompleteCount = 0;
  for (const p of positions) {
    // K/P headline counts closed rows only (VenuePnlInput.pendingCount); an open position is not realized.
    if (p.status === 'open') continue;
    closedCount += 1;
    if (p.totalRealizedNetUsd === null) {
      if (p.evidence === 'payout_unverified') pendingPayoutCount += 1;
      else incompleteCount += 1;
      continue;
    }
    total += units(p.totalRealizedNetUsd);
  }
  const pendingCount = pendingPayoutCount + incompleteCount;
  return { netPnlCents: pendingCount > 0 ? null : toCents(total), closedCount, pendingCount, pendingPayoutCount, incompleteCount };
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The US section's notes: which US values are unknown, and why, and how many executions were withheld. */
export function usPnlNotes(summary: Pick<UsPnlSummary, 'pendingPayoutCount' | 'incompleteCount'>, withheld: number): string[] {
  const notes: string[] = [];
  if (summary.pendingPayoutCount > 0) {
    notes.push(`${plural(summary.pendingPayoutCount, 'settled US position', 'settled US positions')}: P&L pending verified payout`);
  }
  if (summary.incompleteCount > 0) {
    notes.push(`${plural(summary.incompleteCount, 'US position', 'US positions')}: P&L incomplete`);
  }
  if (withheld > 0) notes.push(`${plural(withheld, 'US execution', 'US executions')} withheld`);
  return notes;
}
